import ErrorOutline from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { hexToRgba } from '~utils/utilFunctions';

import useBillingBannerState from './useBillingBannerState';

// Tread's accounts-receivable inbox. The banner is a collections nudge, so the
// only action points the company's admin at the team that can take payment.
const BILLING_CONTACT_EMAIL = 'finance@tread.io';

const TIER_CONFIG = {
  overdue: {
    bodyKey: 'billing.account_overdue_body',
    colorToken: 'treadOrangeDark',
    Icon: WarningAmberOutlined,
    role: 'status',
    titleKey: 'billing.account_overdue_title',
  },
  suspended: {
    bodyKey: 'billing.account_suspended_body',
    colorToken: 'treadRed',
    Icon: ErrorOutline,
    role: 'alert',
    titleKey: 'billing.account_suspended_title',
  },
} as const;

interface ContactEmailArgs {
  companyId: string;
  companyName: string;
  userEmail: Nullable<string>;
  userName: string;
}

function buildContactEmailHref({
  companyId,
  companyName,
  userEmail,
  userName,
}: ContactEmailArgs) {
  const subject = t('billing.contact_email_subject', { company: companyName });
  const body = t('billing.contact_email_body', {
    company: companyName,
    companyId,
    email: userEmail,
    name: userName,
  });

  return `mailto:${BILLING_CONTACT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

const BillingStatusBanner = () => {
  const { userStore } = useStores();
  const { user, userCompany } = userStore;

  const tier = useBillingBannerState();

  if (!tier) {
    return null;
  }

  const { bodyKey, colorToken, Icon, role, titleKey } = TIER_CONFIG[tier];

  const contactEmailHref = buildContactEmailHref({
    companyId: userCompany.id,
    companyName: userCompany.legalName,
    userEmail: user.email,
    userName: user.fullName,
  });

  return (
    <Box
      role={role}
      sx={(theme) => ({
        alignItems: 'center',
        bgcolor: hexToRgba(theme.brandV2.colors[colorToken], 0.1),
        borderBottom: `1px solid ${hexToRgba(theme.brandV2.colors[colorToken], 0.25)}`,
        display: 'flex',
        gap: 1.5,
        // Match the page content's horizontal padding (main uses px: 5) so the
        // CTA's right edge lines up with the right edge of the page content.
        px: 5,
        py: 1,
      })}
    >
      <Icon
        sx={(theme) => ({ color: theme.brandV2.colors[colorToken], fontSize: '20px' })}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={(theme) => ({ color: theme.brandV2.colors[colorToken], fontWeight: 700 })}
        >
          {t(titleKey)}
        </Typography>
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray2 })}
        >
          {t(bodyKey)}
        </Typography>
      </Box>

      <SmallButton
        color="brandV2Black"
        href={contactEmailHref}
        variant="contained"
        sx={(theme) => ({ color: theme.palette.common.white, flexShrink: 0 })}
      >
        {t('billing.contact_billing')}
      </SmallButton>
    </Box>
  );
};

export default observer(BillingStatusBanner);
